import React from "react"
import { cn } from "@/lib/utils"

interface PageHeaderProps {
  /** Title text - accepts a string or JSX (e.g. with inline highlights) */
  title: React.ReactNode
  subtitle?: string
  /** Extra Tailwind classes applied to the subtitle paragraph */
  subtitleClassName?: string
  /** Right-side slot - stats block, back button, or action button */
  children?: React.ReactNode
  className?: string
}

/**
 * Standard page header used at the top of every main page and dashboard.
 * Renders: h1 title + olive accent bar + optional subtitle.
 * Pass children to populate the right-side slot (stats, actions, back button).
 *
 * @example - simple
 * <PageHeader title="Admin Dashboard" />
 *
 * @example - with subtitle and right-side action
 * <PageHeader title="My Orders" subtitle="STATUS & TRACKING">
 *   <Button>New Order</Button>
 * </PageHeader>
 */
export function PageHeader({
  title,
  subtitle,
  subtitleClassName,
  children,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        "mb-12 flex flex-col items-end gap-6 border-b-2 border-foreground/5 pb-8 md:flex-row md:justify-between",
        className
      )}
    >
      <div className="space-y-4">
        <h1 className="font-display text-5xl leading-none tracking-tighter text-foreground">
          {title}
        </h1>
        <div className="h-1 w-12 bg-primary" />
        {subtitle && (
          <p
            className={cn(
              "text-sm font-medium uppercase tracking-widest text-muted-foreground",
              subtitleClassName
            )}
          >
            {subtitle}
          </p>
        )}
      </div>
      {children && <div className="flex-shrink-0">{children}</div>}
    </header>
  )
}

